import { useEffect, useId, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { Text } from '@querodelivery/uni-react';
import {
  IN_PROGRESS_TIME_LABEL,
  ROUTE_MANAGER_ACTION_CAPTION,
} from '../../constants';
import type { RouteFocusTarget, RouteSequenceItem } from '../../types';
import styles from './route-sequence.module.scss';

interface RouteSequenceParams {
  items: RouteSequenceItem[];
  focusedTarget: RouteFocusTarget;
  onFocus: (target: RouteFocusTarget) => void;
  collapsible?: boolean;
  expandRevision?: number;
}

export const RouteSequence = ({
  items,
  focusedTarget,
  onFocus,
  collapsible = false,
  expandRevision = 0,
}: RouteSequenceParams) => {
  const listId = useId();
  const [isExpanded, setIsExpanded] = useState(!collapsible);

  useEffect(() => {
    if (expandRevision > 0) {
      setIsExpanded(true);
    }
  }, [expandRevision]);

  if (items.length === 0) {
    return null;
  }

  const heading = (
    <Text as="span" tone="supporting" fontSize="sm" fontWeight="semibold">
      Sequência da entrega
    </Text>
  );

  return (
    <div className={styles.sequence} data-expanded={isExpanded}>
      {collapsible ? (
        <button
          type="button"
          className={`${styles.heading} ${styles.headingToggle}`}
          aria-expanded={isExpanded}
          aria-controls={listId}
          onClick={() => setIsExpanded(expanded => !expanded)}
        >
          {heading}
          <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
        </button>
      ) : (
        <div className={styles.heading}>{heading}</div>
      )}
      <div className={styles.collapse} id={listId} hidden={!isExpanded}>
        <ol className={styles.list}>
          {items.map(item => {
            const isFocused = focusedTarget === item.target;
            const isWaypoint = item.symbol !== 'action';

            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`${styles.step} ${
                    isFocused ? styles.stepFocused : ''
                  } ${item.inProgress ? styles.stepInProgress : ''}`}
                  onClick={() => onFocus(item.target)}
                >
                  <span className={styles.marker} aria-hidden>
                    <span
                      className={`${styles.pin} ${styles[item.symbol]} ${
                        isWaypoint ? styles.waypoint : ''
                      } ${
                        item.executedByManager ? styles.executedByManager : ''
                      } ${item.inProgress ? styles.inProgress : ''}`}
                    />
                  </span>
                  <span className={styles.copy}>
                    <Text
                      as="span"
                      fontSize="sm"
                      fontWeight={isWaypoint ? 'semibold' : 'medium'}
                      tone={isFocused || isWaypoint ? 'highlight' : 'secondary'}
                    >
                      {item.title}
                    </Text>
                    {item.executedByManager && (
                      <Text
                        as="span"
                        className={styles.managerCaption}
                        fontSize="xxs"
                        fontWeight="semibold"
                      >
                        {ROUTE_MANAGER_ACTION_CAPTION}
                      </Text>
                    )}
                    {item.caption && (
                      <Text as="span" fontSize="xxs" tone="supporting">
                        {item.caption}
                      </Text>
                    )}
                    {item.address && (
                      <Text as="span" fontSize="xs" tone="supporting">
                        {item.address}
                      </Text>
                    )}
                  </span>
                  {(item.time || item.inProgress) && (
                    <span
                      className={`${styles.time} ${
                        item.inProgress ? styles.timeInProgress : ''
                      }`}
                    >
                      <Text as="span" fontSize="xs" fontWeight="semibold">
                        {item.inProgress ? IN_PROGRESS_TIME_LABEL : item.time}
                      </Text>
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};
